import jsPDF from 'jspdf';
import { bandwidthExportDateFileSuffix } from '../bandwidth/bandwidthExportFileNaming';
import {
  captureMsrElement,
  MSR_SECTION_CONFIG,
  MSR_SECTION_ORDER,
  msrSectionFilename,
  withMsrExportCapture,
  type MsrSectionKey,
} from './msrReportSectionExport';
import {
  msrObservationWordTokens,
  parseMsrObservationSegments,
  type MsrWordToken,
} from './msrObservationParser';
import type { MsrReportData } from './msrReportTypes';

export type MsrPdfExport = { blob: Blob; fileName: string };

const MSR_BLUE = { r: 52, g: 101, b: 164 };
const SLATE_600 = { r: 71, g: 85, b: 105 };
const SLATE_400 = { r: 148, g: 163, b: 184 };
const OBS_BG = { r: 248, g: 250, b: 252 };

const MARGIN = 12;
const CHART_GAP = 6;
const MAX_CHART_H = 82;

function msrPdfFilename(customerName: string, dateFrom: string, dateTo: string): string {
  const safe = customerName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'customer';
  return `MSR_Report_${safe}_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}.pdf`;
}

function pdfAsciiSafeText(s: string): string {
  return s
    .replace(/\u2192/g, ' to ')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/…/g, '...');
}

function sectionObservations(section: MsrSectionKey, data: MsrReportData): string[] {
  switch (section) {
    case 'team-ticket':
      return data.teamTicketDistribution.observations;
    case 'resolution-priority':
      return data.resolutionPriorityDistribution.observations;
    case 'ci-sla':
      return data.ciSlaDistribution.observations;
    default:
      return [];
  }
}

function drawSlideHeader(
  doc: jsPDF,
  customerName: string,
  reportTitle: string,
  contentW: number
): number {
  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(MSR_BLUE.r, MSR_BLUE.g, MSR_BLUE.b);
  const customerLines = doc.splitTextToSize(pdfAsciiSafeText(customerName.toUpperCase()), contentW);
  doc.text(customerLines, MARGIN, y + 6);
  y += customerLines.length * 7 + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);
  const titleLines = doc.splitTextToSize(pdfAsciiSafeText(reportTitle.toUpperCase()), contentW);
  doc.text(titleLines, MARGIN, y + 4);
  y += titleLines.length * 5 + 4;

  doc.setTextColor(0, 0, 0);
  return y;
}

function drawChartPair(
  doc: jsPDF,
  left: Awaited<ReturnType<typeof captureMsrElement>>,
  right: Awaited<ReturnType<typeof captureMsrElement>>,
  y: number,
  contentW: number
): number {
  const chartW = (contentW - CHART_GAP) / 2;
  const leftH = left ? Math.min(MAX_CHART_H, chartW / left.aspectRatio) : 0;
  const rightH = right ? Math.min(MAX_CHART_H, chartW / right.aspectRatio) : 0;
  const rowH = Math.max(leftH, rightH, 36);

  const caps = [left, right] as const;
  caps.forEach((cap, index) => {
    const x = MARGIN + index * (chartW + CHART_GAP);

    if (cap?.dataUrl) {
      let imgH = Math.min(rowH, chartW / cap.aspectRatio);
      let imgW = imgH * cap.aspectRatio;
      if (imgW > chartW) {
        imgW = chartW;
        imgH = imgW / cap.aspectRatio;
      }
      const ox = x + (chartW - imgW) / 2;
      const oy = y + (rowH - imgH) / 2;
      doc.addImage(cap.dataUrl, 'PNG', ox, oy, imgW, imgH);
    }
  });

  return y + rowH + 5;
}

type PdfObsLine = MsrWordToken[];

function measureTokenWidth(doc: jsPDF, token: MsrWordToken, fontSize: number): number {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
  return doc.getTextWidth(pdfAsciiSafeText(token.text));
}

function wrapObservationParagraph(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  fontSize: number,
  bulletPrefix: string
): PdfObsLine[] {
  const tokens = msrObservationWordTokens(parseMsrObservationSegments(text));
  const lines: PdfObsLine[] = [];
  let current: MsrWordToken[] = [];
  let currentW = 0;

  const flush = () => {
    if (current.length > 0) {
      lines.push(current);
      current = [];
      currentW = 0;
    }
  };

  const startContinuation = () => {
    const indent: MsrWordToken = { text: '   ', bold: false };
    current = [indent];
    currentW = measureTokenWidth(doc, indent, fontSize);
  };

  if (bulletPrefix) {
    const bullet: MsrWordToken = { text: bulletPrefix, bold: false };
    current.push(bullet);
    currentW = measureTokenWidth(doc, bullet, fontSize);
  }

  for (const token of tokens) {
    const w = measureTokenWidth(doc, token, fontSize);
    if (currentW + w > maxWidth && current.length > 0) {
      flush();
      if (bulletPrefix) startContinuation();
    }
    current.push(token);
    currentW += w;
  }
  flush();
  return lines;
}

function drawObservationLine(
  doc: jsPDF,
  line: PdfObsLine,
  x: number,
  y: number,
  fontSize: number
): void {
  let cx = x;
  for (const token of line) {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
    const safe = pdfAsciiSafeText(token.text);
    doc.text(safe, cx, y);
    cx += doc.getTextWidth(safe);
  }
}

function drawObservationBox(
  doc: jsPDF,
  observations: string[],
  y: number,
  contentW: number,
  pageH: number
): void {
  if (observations.length === 0) return;

  const boxX = MARGIN;
  const boxW = contentW;
  const headerH = 7;
  const padX = 4;
  const padY = 3;
  const lineH = 4.2;
  const fontSize = 8.5;
  const textW = boxW - padX * 2 - 2;

  const allLines: PdfObsLine[] = [];
  for (let o = 0; o < observations.length; o++) {
    const paraLines = wrapObservationParagraph(doc, observations[o]!, textW, fontSize, '• ');
    allLines.push(...paraLines);
    if (o < observations.length - 1) allLines.push([]);
  }
  while (allLines.length > 0 && allLines[allLines.length - 1]?.length === 0) {
    allLines.pop();
  }

  const contentH = allLines.length * lineH + padY * 2;
  const boxH = headerH + contentH;
  const maxBoxH = pageH - MARGIN - 10 - y;
  const drawH = Math.min(boxH, maxBoxH);

  doc.setFillColor(OBS_BG.r, OBS_BG.g, OBS_BG.b);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.roundedRect(boxX, y, boxW, drawH, 2, 2, 'FD');

  doc.setFillColor(MSR_BLUE.r, MSR_BLUE.g, MSR_BLUE.b);
  doc.roundedRect(boxX + 1, y + 1, 36, headerH - 1, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('Observation', boxX + 4, y + 5.2);

  doc.setFontSize(fontSize);
  doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);

  let textY = y + headerH + padY + 2.5;
  const maxLines = Math.floor((drawH - headerH - padY * 2) / lineH);
  for (let i = 0; i < Math.min(allLines.length, maxLines); i++) {
    const line = allLines[i]!;
    if (line.length > 0) drawObservationLine(doc, line, boxX + padX + 1, textY, fontSize);
    textY += lineH;
  }

  doc.setTextColor(0, 0, 0);
}

function drawSlideFooter(doc: jsPDF, pageW: number, pageH: number, pageNum: number): void {
  const footerY = pageH - MARGIN + 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(SLATE_400.r, SLATE_400.g, SLATE_400.b);
  doc.text(
    pdfAsciiSafeText(`Copyright © ${new Date().getFullYear()} Tenarai. All Rights Reserved.`),
    MARGIN,
    footerY
  );
  doc.text(String(pageNum), pageW - MARGIN, footerY, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

async function renderMsrSectionToPdf(
  doc: jsPDF,
  section: MsrSectionKey,
  data: MsrReportData,
  pageNum: number,
  addPage: boolean
): Promise<void> {
  if (addPage) doc.addPage();

  const config = MSR_SECTION_CONFIG[section];
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;

  let y = drawSlideHeader(doc, data.customerName, config.title, contentW);

  let leftCap: Awaited<ReturnType<typeof captureMsrElement>> = null;
  let rightCap: Awaited<ReturnType<typeof captureMsrElement>> = null;
  [leftCap, rightCap] = await Promise.all([
    captureMsrElement(config.charts[0].id),
    captureMsrElement(config.charts[1].id),
  ]);

  y = drawChartPair(doc, leftCap, rightCap, y + 2, contentW);
  drawObservationBox(doc, sectionObservations(section, data), y, contentW, pageH);
  drawSlideFooter(doc, pageW, pageH, pageNum);
}

/** Export a single MSR section as a presentation-style landscape PDF slide. */
export async function exportMsrSectionPdf(
  section: MsrSectionKey,
  data: MsrReportData,
  dateFrom: string,
  dateTo: string
): Promise<MsrPdfExport> {
  return withMsrExportCapture(async () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    await renderMsrSectionToPdf(doc, section, data, 1, false);

    return {
      blob: doc.output('blob'),
      fileName: msrSectionFilename(configFileSlug(section), data.customerName, dateFrom, dateTo, 'pdf'),
    };
  });
}

function configFileSlug(section: MsrSectionKey): string {
  return MSR_SECTION_CONFIG[section].fileSlug;
}

/** Export all MSR sections as a multi-page presentation-style PDF. */
export async function exportMsrReportPdf(
  data: MsrReportData,
  dateFrom: string,
  dateTo: string
): Promise<MsrPdfExport> {
  return withMsrExportCapture(async () => {
    const doc = new jsPDF('l', 'mm', 'a4');

    for (let i = 0; i < MSR_SECTION_ORDER.length; i++) {
      await renderMsrSectionToPdf(doc, MSR_SECTION_ORDER[i]!, data, i + 1, i > 0);
    }

    return {
      blob: doc.output('blob'),
      fileName: msrPdfFilename(data.customerName, dateFrom, dateTo),
    };
  });
}
